import React, { useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ScheduleRow from './ScheduleRow';
import IconPicker from './IconPicker';
import RowStyleMenu from './RowStyleMenu';
import AddMenu from './AddMenu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { GripVertical, Plus, Trash2, X } from 'lucide-react';

const MIN_COL = 8; // Mindestbreite in %

const genId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now()) + Math.random().toString(16).slice(2);

const normalizeWidths = (cols) => {
  const sum = cols.reduce((a, c) => a + (c.width || 0), 0) || 1;
  return cols.map((c) => ({ ...c, width: Math.max(MIN_COL, Math.round(((c.width || 0) / sum) * 100)) }));
};

function DaySection({ day, columns, onUpdateColumns, onUpdateDay, onRemoveDay, canRemove }) {
  const cols = Array.isArray(columns) && columns.length ? columns : [];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: day.id, data: { type: 'section' } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  // Tag als Ablage-Ziel für Zeilen (ermöglicht Drop in leere Tage / ans Ende)
  const { setNodeRef: setRowsDroppableRef } = useDroppable({
    id: `container:${day.id}`,
    data: { type: 'container', dayId: day.id },
  });

  const tableRef = useRef(null);

  // ----- Spalten-Operationen -----
  const renameColumn = (idx, label) => {
    if (!onUpdateColumns) return;
    onUpdateColumns(cols.map((c, i) => (i === idx ? { ...c, label } : c)));
  };

  const addColumn = () => {
    if (!onUpdateColumns) return;
    onUpdateColumns(normalizeWidths([...cols, { id: genId(), label: 'Neu', width: 12 }]));
  };

  const deleteColumn = (idx) => {
    if (!onUpdateColumns || cols.length <= 1) return;
    onUpdateColumns(normalizeWidths(cols.filter((_, i) => i !== idx)));
  };

  // Spaltenbreite per Ziehen am rechten Rand (verschiebt mit der Nachbarspalte).
  const startResize = (e, idx) => {
    if (!onUpdateColumns) return;
    const nextIdx = idx + 1;
    if (nextIdx >= cols.length) return;
    e.preventDefault();
    e.stopPropagation();
    const tableWidth = tableRef.current ? tableRef.current.offsetWidth : 1000;
    const startX = e.clientX;
    const base = cols.map((c) => ({ ...c }));

    const onMove = (ev) => {
      const deltaPct = ((ev.clientX - startX) / tableWidth) * 100;
      let left = (base[idx].width || 0) + deltaPct;
      let right = (base[nextIdx].width || 0) - deltaPct;
      if (left < MIN_COL) { right -= (MIN_COL - left); left = MIN_COL; }
      if (right < MIN_COL) { left -= (MIN_COL - right); right = MIN_COL; }
      const updated = base.map((c, i) =>
        i === idx ? { ...c, width: Math.round(left) } : i === nextIdx ? { ...c, width: Math.round(right) } : c
      );
      onUpdateColumns(updated);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ----- Zeilen-Operationen -----
  const handleAddRow = (type = 'item') => {
    const newRow = { id: genId(), type, text: '' };
    onUpdateDay({ ...day, rows: [...day.rows, newRow] });
  };

  const handleRemoveRow = (rowId) => {
    onUpdateDay({ ...day, rows: day.rows.filter((row) => row.id !== rowId) });
  };

  const handleUpdateRow = (rowId, updatedRow) => {
    onUpdateDay({ ...day, rows: day.rows.map((row) => (row.id === rowId ? updatedRow : row)) });
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, ...(day.color ? { borderLeft: `5px solid ${day.color}` } : {}) }}
      className="schedule-day mb-8 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      {/* Day Header */}
      <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            className="drag-handle cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 no-print"
            data-testid="day-drag-handle"
          >
            <GripVertical className="h-5 w-5" />
          </div>
          <IconPicker
            currentIcon={day.icon || 'calendar'}
            onSelectIcon={(iconName) => onUpdateDay({ ...day, icon: iconName })}
          />
          <Input
            type="text"
            value={day.date}
            onChange={(e) => onUpdateDay({ ...day, date: e.target.value })}
            className="font-semibold text-slate-900 w-40 border-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-600"
            placeholder="DD-MM-YYYY"
            data-testid="day-date-input"
          />
        </div>
        <div className="flex items-center gap-1 no-print">
          <RowStyleMenu
            showFormat={false}
            color={day.color || null}
            onChange={(s) => onUpdateDay({ ...day, color: s.color })}
          />
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveDay}
              className="text-slate-600 hover:text-red-600"
              data-testid="remove-day-button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Schedule Table */}
      <div className="overflow-visible">
        <table ref={tableRef} className="schedule-table w-full">
          <thead>
            <tr>
              <th className="no-print" style={{ width: '40px' }}></th>
              {cols.map((col, idx) => (
                <th key={col.id} style={{ width: `${col.width}%` }} className="group">
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={col.label}
                      onChange={(e) => renameColumn(idx, e.target.value)}
                      className="font-semibold text-xs bg-transparent border-none w-full focus:outline-none focus:ring-1 focus:ring-blue-600 px-1 rounded"
                      placeholder="Spalte"
                    />
                    {cols.length > 1 && (
                      <button
                        type="button"
                        onClick={() => deleteColumn(idx)}
                        className="no-print opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0"
                        title="Spalte löschen"
                        data-testid="delete-column-button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {idx < cols.length - 1 && (
                    <div
                      className="col-resizer no-print"
                      onMouseDown={(e) => startResize(e, idx)}
                      onClick={(e) => e.stopPropagation()}
                      title="Breite ziehen"
                    />
                  )}
                </th>
              ))}
              <th className="no-print" style={{ width: '88px' }}>
                <button
                  type="button"
                  onClick={addColumn}
                  className="text-slate-400 hover:text-blue-600 flex items-center justify-center w-full"
                  title="Spalte hinzufügen"
                  data-testid="add-column-button"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody ref={setRowsDroppableRef}>
            <SortableContext
              items={day.rows.map((row) => row.id)}
              strategy={verticalListSortingStrategy}
            >
              {day.rows.map((row) => (
                <ScheduleRow
                  key={row.id}
                  row={row}
                  columns={cols}
                  dayId={day.id}
                  onUpdateRow={(updatedRow) => handleUpdateRow(row.id, updatedRow)}
                  onRemoveRow={() => handleRemoveRow(row.id)}
                  canRemove={day.rows.length > 1}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </div>

      {/* Add-Menü (+) */}
      <div className="flex gap-2 p-4 no-print">
        <AddMenu
          onAddRow={() => handleAddRow('item')}
          onAddText={() => handleAddRow('text')}
          onAddColumn={addColumn}
        />
      </div>
    </div>
  );
}

export default DaySection;
